import Link from '@/components/common/AccessLink';
import StatusBadge from '@/components/common/StatusBadge';
import PermissionGate from '@/components/common/PermissionGate';
import PaymentMethodBadge, { getSalePaymentMethods } from '@/components/common/PaymentMethodBadge';
import SaleWhatsAppAction from './SaleWhatsAppAction';
import SaleSelectionCheckbox from './SaleSelectionCheckbox';
import type { SaleWithCustomer } from '@/hooks/useSales';
import { financialSummary } from '@/lib/salesOverview';
import { saleDateLabel, saleMoney } from '@klothiq/domain';
import { Pencil, Trash2, ArrowUpRight } from 'lucide-react';

export default function SalesRecords({
  rows,
  showCreator = false,
  onDelete,
  selection,
}: {
  rows: SaleWithCustomer[];
  showCreator?: boolean;
  onDelete?: (sale: SaleWithCustomer) => void;
  selection?: {
    ids: ReadonlySet<string>;
    disabled: boolean;
    onToggle: (id: string) => void;
    onTogglePage: () => void;
  };
}) {
  function actions(s: SaleWithCustomer) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-1">
        <SaleWhatsAppAction saleId={s.id} />
        <Link
          to={`/sales/${s.id}`}
          aria-label={`View ${s.sale_number}`}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-muted"
        >
          <ArrowUpRight className="h-4 w-4" />
        </Link>
        <PermissionGate permission="sales.edit">
          <Link
            to={`/sales/${s.id}/edit`}
            aria-label={`Edit ${s.sale_number}`}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-muted"
          >
            <Pencil className="h-4 w-4" />
          </Link>
        </PermissionGate>
        {onDelete && (
          <PermissionGate permission="sales.delete">
            <button
              type="button"
              onClick={() => onDelete(s)}
              aria-label={`Move ${s.sale_number} to Recycle Bin`}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        )}
      </div>
    );
  }
  function due(s: SaleWithCustomer) {
    const f = financialSummary(s);
    return f.return_review ? (
      <span className="text-xs text-amber-600 dark:text-amber-400">Review return</span>
    ) : f.amount_due === null ? (
      <span className="text-muted-foreground">Not applicable</span>
    ) : (
      <span className={f.amount_due > 0 ? 'text-destructive' : ''}>{saleMoney(f.amount_due)}</span>
    );
  }
  function badges(s: SaleWithCustomer) {
    return (
      <div className="flex flex-wrap gap-1">
        <StatusBadge status={s.sale_status} />
        <StatusBadge status={s.payment_status} />
        {s.return_status && <StatusBadge status={`return_${s.return_status}`} size="sm" />}
      </div>
    );
  }
  return (
    <>
      <div className="obsidian-surface hidden overflow-x-auto rounded-xl border border-border bg-card md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {selection && (
                <th className="w-12 px-2 py-1">
                  <SaleSelectionCheckbox
                    label="Select all sales on this page"
                    checked={rows.length > 0 && rows.every((sale) => selection.ids.has(sale.id))}
                    mixed={
                      rows.some((sale) => selection.ids.has(sale.id)) &&
                      !rows.every((sale) => selection.ids.has(sale.id))
                    }
                    disabled={selection.disabled}
                    onChange={selection.onTogglePage}
                  />
                </th>
              )}
              {[
                'Sale / customer',
                'Seller',
                ...(showCreator ? ['Entered by'] : []),
                'Invoice',
                'Net collected',
                'Known due',
                'Status / payment',
                'Actions',
              ].map((x) => (
                <th key={x} className="whitespace-nowrap px-4 py-3 font-medium">
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((s) => (
              <tr
                key={s.id}
                className={
                  selection?.ids.has(s.id)
                    ? 'bg-primary/5 hover:bg-primary/10'
                    : 'hover:bg-muted/20'
                }
              >
                {selection && (
                  <td className="px-2 py-1">
                    <SaleSelectionCheckbox
                      label={`Select sale ${s.sale_number}`}
                      checked={selection.ids.has(s.id)}
                      disabled={selection.disabled}
                      onChange={() => selection.onToggle(s.id)}
                    />
                  </td>
                )}
                <td className="min-w-48 px-4 py-3">
                  <Link to={`/sales/${s.id}`} className="font-semibold text-primary">
                    {s.sale_number}
                  </Link>
                  <p className="mt-1 font-medium">
                    {s.customer_name || s.customers?.name || 'Walk-in Customer'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.customer_mobile || s.customers?.phone || 'No phone'} ·{' '}
                    {saleDateLabel(s.sale_date)}
                  </p>
                </td>
                <td className="min-w-36 px-4 py-3">
                  {s.seller_name || 'Seller not recorded'}
                  <p className="text-xs text-muted-foreground">
                    {s.sold_by_staff_id ? 'Staff seller' : 'Login seller'}
                  </p>
                </td>
                {showCreator && (
                  <td className="px-4 py-3">{s.entered_by_name || 'Creator not recorded'}</td>
                )}
                <td className="whitespace-nowrap px-4 py-3 font-semibold">
                  {saleMoney(s.total_amount)}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {saleMoney(financialSummary(s).net_collected)}
                  {financialSummary(s).refunded > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Refunded {saleMoney(financialSummary(s).refunded)}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">{due(s)}</td>
                <td className="min-w-44 px-4 py-3">
                  {badges(s)}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {getSalePaymentMethods(s).map((m) => (
                      <PaymentMethodBadge key={m} method={m} size="sm" />
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3">{actions(s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 md:hidden">
        {selection && (
          <div className="flex items-center gap-1">
            <SaleSelectionCheckbox
              label="Select all sales on this page"
              checked={rows.length > 0 && rows.every((sale) => selection.ids.has(sale.id))}
              mixed={
                rows.some((sale) => selection.ids.has(sale.id)) &&
                !rows.every((sale) => selection.ids.has(sale.id))
              }
              disabled={selection.disabled}
              onChange={selection.onTogglePage}
            />
            <span className="text-sm">Select this page</span>
          </div>
        )}
        {rows.map((s) => (
          <article
            key={s.id}
            className={`obsidian-surface space-y-3 rounded-xl border bg-card p-4 ${selection?.ids.has(s.id) ? 'border-primary/50 ring-1 ring-primary/20' : 'border-border'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {selection && (
                    <SaleSelectionCheckbox
                      label={`Select sale ${s.sale_number}`}
                      checked={selection.ids.has(s.id)}
                      disabled={selection.disabled}
                      onChange={() => selection.onToggle(s.id)}
                    />
                  )}
                  <Link to={`/sales/${s.id}`} className="font-semibold text-primary">
                    {s.sale_number}
                  </Link>
                </div>
                <h3 className="mt-1 break-words font-semibold">
                  {s.customer_name || s.customers?.name || 'Walk-in Customer'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {saleDateLabel(s.sale_date)} ·{' '}
                  {s.customer_mobile || s.customers?.phone || 'No phone'}
                </p>
              </div>
              <span className="shrink-0 text-sm font-bold">{saleMoney(s.total_amount)}</span>
            </div>
            <div className="rounded-lg bg-muted/30 px-3 py-2 text-sm">
              <p>
                Seller: <strong>{s.seller_name || 'Seller not recorded'}</strong>
              </p>
              {showCreator && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Entered by: {s.entered_by_name || 'Creator not recorded'}
                </p>
              )}
            </div>
            {badges(s)}
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Net collected</dt>
                <dd className="mt-1 font-medium">{saleMoney(financialSummary(s).net_collected)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Known due</dt>
                <dd className="mt-1 font-medium">{due(s)}</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-1">
              {getSalePaymentMethods(s).map((m) => (
                <PaymentMethodBadge key={m} method={m} size="sm" />
              ))}
            </div>
            <div className="border-t border-border pt-2">{actions(s)}</div>
          </article>
        ))}
      </div>
    </>
  );
}
